
import _ from 'lodash';
import IAPI from 'core/src/api-client-abstract';
import ApplicationInfo from 'core/src/application-info';
import Language from "core/src/language";
import VueViewComponent from "client/src/vue-view/vue-view-component.vue";
import ViewCommon from "client/src/vue-view/view-common.vue";
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/javascript/javascript.js';
import {isoDate, isoDatetime} from 'core/src/utils/legacy';
import MaskedInput from '../input-view/MaskedInput.vue';
const { isDurationValue, parseDurationObject } = require('./duration-utils');

const typeProperties = {
	Date: ['day', 'month', 'year'],
	DateTime: ['year', 'month', 'day', 'hour', 'minute' , 'second' , 'nanosecond' , 'timeZoneOffsetSeconds' , 'timeZoneId'],
	DateTimeAlt: ['year', 'month', 'day', 'hour', 'minute' , 'second' , 'nanosecond' , 'timeZoneOffsetSeconds'],
	Duration: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds', 'nanoseconds'],
	ValidDuration: ['months', 'days', 'seconds', 'nanoseconds'],
	ValidDurationMemgraph: ['days', 'seconds', 'nanoseconds'],
	ValidDurationFalkorDB: ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'],
	LocalDateTime: ['year', 'month', 'day', 'hour', 'minute' , 'second' , 'nanosecond'],
	LocalTime: ['hour', 'minute' , 'second' , 'nanosecond'],
	Time: ['hour', 'minute' , 'second' , 'nanosecond' , 'timeZoneOffsetSeconds'],
	ValidPoint2D: ['srid', 'x', 'y'],
	Point2D: ['srid', 'x', 'y', 'latitude', 'longitude'],
	ValidPoint3D: ['srid', 'x', 'y', 'z'],
	Point3D: ['srid', 'x', 'y', 'z', 'latitude', 'longitude', 'height'],
	Location: ['latitude', 'longitude'],
}

const allDataTypeOptions = [
	{ value: 'auto', label: 'Auto', title: 'Auto detect' },
	{ value: 'string', label: 'Text', title: 'String' },
	{ value: 'number', label: 'Number', title: 'Number' },
	{ value: 'boolean', label: 'Boolean', title: 'Boolean' },
	{ value: 'date', label: 'Date', title: 'Date' },
	{ value: 'localtime', label: 'Local time', title: 'Time' },
	{ value: 'time', label: 'Zoned time', title: 'Time' },
	{ value: 'localdatetime', label: 'Local date-time', title: 'Date-Time' },
	{ value: 'datetime', label: 'Zoned date time', title: 'Date-Time' },
	{ value: 'object', label: 'Object', title: 'Object' },
	{ value: 'array', label: 'Array', title: 'Array' },
	{ value: 'json', label: 'JSON', title: 'JSON' },
	{ value: 'point2d', label: 'Point 2D', title: 'Point 2D' },
	{ value: 'point3d', label: 'Point 3D', title: 'Point 3D' },
	{ value: 'location', label: 'Location', title: 'Geographic coordinates (latitude, longitude)' },
	{ value: 'duration', label: 'Duration', title: 'Duration' },
	{ value: 'fileupload', label: 'File upload', title: 'File upload' },
];

const driverUnsupportedTypes = {
	falkordb: ['time', 'datetime', 'point2d', 'point3d', 'object'],
	neo4j: ['object'],
};

const driverOnlyTypes = {
	location: ['falkordb'],
};

const zonedDateTimeReg = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})[T ](?<hour>\d{2}):(?<minute>\d{2})(:(?<second>\d{2}))?(\.(?<nanosecond>\d{1,9}))?\s?(?<tz>Z|[+-]\d{2}:\d{2})$/;

const zonedTimeReg = /^(?<hour>\d{2}):(?<minute>\d{2})(:(?<second>\d{2}))?(\.(?<nanosecond>\d{1,9}))?\s?(?<tz>Z|[+-]\d{2}:\d{2})$/;

const localDateTimeReg = /^\d{4}[-./]\d{2}[-./]\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;

const localTimeReg = /^\d{2}:\d{2}(:\d{2})?$/;

const dateReg = /^\d{4}[-./]\d{2}[-./]\d{2}$/;

const wkt2DReg = /^(?:SRID=(?<srid>\d+);)?POINT\s*\(\s*(?<x>-?\d+(?:\.\d+)?)\s+(?<y>-?\d+(?:\.\d+)?)\s*\)$/i;

const cypherPoint2DReg = /^point\s*\(\s*\{\s*(?:srid\s*:\s*(?<srid>\d+)\s*,\s*)?(?:x\s*:\s*(?<x>-?\d+(?:\.\d+)?)|longitude\s*:\s*(?<lon>-?\d+(?:\.\d+)?))\s*,\s*(?:y\s*:\s*(?<y>-?\d+(?:\.\d+)?)|latitude\s*:\s*(?<lat>-?\d+(?:\.\d+)?))\s*\}\s*\)$/i;

const driverPoint2DReg = /^Point\{srId=(?<srid>\d+),\s*x=(?<x>-?\d+(?:\.\d+)?),\s*y=(?<y>-?\d+(?:\.\d+)?)\}$/;

const wkt3DReg = /^(?:SRID=(?<srid>\d+);)?POINT\s+Z\s*\(\s*(?<x>-?\d+(?:\.\d+)?)\s+(?<y>-?\d+(?:\.\d+)?)\s+(?<z>-?\d+(?:\.\d+)?)\s*\)$/i;

const cypherPoint3DReg = /^point\s*\(\s*\{\s*(?:srid\s*:\s*(?<srid>\d+)\s*,\s*)?(?:x\s*:\s*(?<x>-?\d+(?:\.\d+)?)|longitude\s*:\s*(?<lon>-?\d+(?:\.\d+)?))\s*,\s*(?:y\s*:\s*(?<y>-?\d+(?:\.\d+)?)|latitude\s*:\s*(?<lat>-?\d+(?:\.\d+)?))\s*,\s*(?:z\s*:\s*(?<z>-?\d+(?:\.\d+)?)|height\s*:\s*(?<h>-?\d+(?:\.\d+)?))\s*\}\s*\)$/i;

const driverPoint3DReg = /^Point\{srId=(?<srid>\d+),\s*x=(?<x>-?\d+(?:\.\d+)?),\s*y=(?<y>-?\d+(?:\.\d+)?),\s*z=(?<z>-?\d+(?:\.\d+)?)\}$/;

/**
 * Checks if an object has exactly the specified properties
 * @param {Object} obj - The object to check
 * @param {Array} props - Array of property names
 * @returns {boolean} True if object has exactly those properties
 */
const hasOnlyProps = (obj, props) => (
		(_.size(obj) == _.size(props)) 
	&&  _.every(props, (key) => _.has(obj, key))
);

/**
 * Checks whether an object's properties form a subset of the allowed keys.
 * In other words, verifies that the object contains only whitelisted properties
 * (it may contain any combination of them, but no extra keys).
 *
 * @param {Object} obj - The object to validate
 * @param {Array<string>} allowedKeys - List of allowed property names
 * @returns {boolean} True if all keys of the object are included in allowedKeys
 */
const hasOnlyPropsSubset = (obj, allowedKeys) => (
	_.isPlainObject(obj) &&
	_.every(_.keys(obj), key => allowedKeys.includes(key))
);

/**
 * Determines the value type for a given value
 * @param {*} value - The value to check
 * @returns {string} The type identifier
 */
function getValueType(value, driver) {
	if (_.isFinite(value)) return 'number';
	if (_.isBoolean(value)) return 'boolean';
	if (_.isArray(value)) return 'array';
	if(_.isObject(value)) {

		if (hasOnlyProps(value, typeProperties.Date)) {
			return 'date'
		}

		if (hasOnlyProps(value, typeProperties.LocalDateTime)) {
			return 'localdatetime'
		}

		if (hasOnlyProps(value, typeProperties.LocalTime)) {
			return 'localtime'
		}

		if (hasOnlyProps(value, typeProperties.Time)) {
			return 'time'
		}

		if (hasOnlyProps(value, typeProperties.DateTime) || hasOnlyProps(value, typeProperties.DateTimeAlt)) {
			return 'datetime'
		}

		if (driver === 'falkordb' && hasOnlyProps(value, typeProperties.Location)) {
			return 'location'
		}

		if (hasOnlyPropsSubset(value, typeProperties.Point2D)) {
			return 'point2d'
		}

		if (hasOnlyPropsSubset(value, typeProperties.Point3D)) {
			return 'point3d'
		}

		if (hasOnlyPropsSubset(value, typeProperties.Duration)) {
			return 'duration'
		}

		return 'object';
	}

	if (_.isString(value)) {
		try {
			let parsed = JSON.parse(value);
			if (_.isArray(parsed) || _.isPlainObject(parsed)) {
				return 'json';
			}
		} catch (e) {
			// Not a valid JSON string
		}
	}

	return 'string';
}

/**
 * Converts a value to JSON string
 * @param {*} value - The value to convert
 * @returns {string} JSON string representation
 */
function toJSON(value) {
	return JSON.stringify(value, null, 2);
}

/**
 * Converts a time object to ISO local time string
 * @param {Object} obj - Time object with hour, minute, second
 * @returns {string} ISO time string
 */
function objectToISOLocalTime(obj) {
	return `${_.padStart(obj.hour, 2, '0')}:${_.padStart(obj.minute, 2, '0')}:${_.padStart(obj.second, 2, '0')}`;
}

/**
 * Converts a string to ISO local time format
 * @param {string} str - Time string
 * @returns {string} ISO formatted time string
 */
function stringToISOLocalTime(str) {
	if (!_.isString(str)) {
		return '';
	}

	let parts = _.split(str, ':');
	return `${_.padStart(parts[0], 2, '0')}:${_.padStart(parts[1], 2, '0')}:${_.padStart(parts[2], 2, '0')}`;
}

/**
 * Parses a time string into a time object
 * @param {string} str - Time string in format "hh:mm:ss.nnnnnnnnn ±hh:mm"
 * @returns {Object|boolean} Time object or false if invalid
 */
function timeStringToObject(str) {
	if (!_.isString(str)) return false;

	const match = str.match(zonedTimeReg);
	if (!match) return false;

	const g = _.get(match, 'groups', {});
	
	const gNumbers = _.mapValues(g, (v, k) => k === 'tz' ? v : _.toNumber(v || 0));
	gNumbers.nanosecond = _.toNumber(_.padEnd(gNumbers.nanosecond.toString(), 9, '0'));

	const timeZoneOffsetSeconds = gNumbers.tz === 'Z'
		? 0
		: (() => {
				const sign = gNumbers.tz.startsWith('-') ? -1 : 1;
				const [h, m] = _.map(gNumbers.tz.slice(1).split(':'), _.toNumber);
				return sign * (h * 3600 + m * 60);
			})();

	return _.assign(_.pick(gNumbers, ['hour','minute','second','nanosecond']), { timeZoneOffsetSeconds });
}

/**
 * Parses a datetime string into a datetime object
 * @param {string} str - DateTime string
 * @returns {Object|boolean} DateTime object or false if invalid
 */
function dateTimeStringToObject(str) {
	if (!_.isString(str)) return false;

	const match = str.match(zonedDateTimeReg);
	if (!match) return false;

	const g = _.get(match, 'groups', {});

	const gNumbers = _.mapValues(g, (v, k) => (k === 'tz' ? v : _.toNumber(v || 0)));
	gNumbers.nanosecond = _.toNumber(_.padEnd(gNumbers.nanosecond.toString(), 9, '0'));

	const timeZoneOffsetSeconds = gNumbers.tz === 'Z'
		? 0
		: (() => {
				const sign = gNumbers.tz.startsWith('-') ? -1 : 1;
				const [h, m] = _.map(gNumbers.tz.slice(1).split(':'), _.toNumber);
				return sign * (h * 3600 + m * 60);
			})();

	return _.assign(
		_.pick(gNumbers, ['year', 'month', 'day', 'hour', 'minute', 'second', 'nanosecond']),
		{ timeZoneOffsetSeconds }
	);
}

/**
 * Converts a time object to string representation
 * @param {Object} object - Time object
 * @returns {string|boolean} Time string or false if invalid
 */
function objectToTimeString(object) {
	if (!hasOnlyProps(object, typeProperties.Time)) {
		return false;
	}

	const total = object.timeZoneOffsetSeconds;
	const sign = total < 0 ? '-' : '+';
	const abs = Math.abs(total);

	const gmtHours = Math.floor(abs / 3600);
	const gmtMinutes = Math.floor((abs % 3600) / 60);

	return (
		`${_.padStart(object.hour, 2, '0')}:` +
		`${_.padStart(object.minute, 2, '0')}:` +
		`${_.padStart(object.second, 2, '0')}.` +
		`${_.padEnd(object.nanosecond, 9, '0')} ` +
		`${sign}${_.padStart(gmtHours, 2, '0')}:${_.padStart(gmtMinutes, 2, '0')}`
	);
}


/**
 * Converts a datetime object to string representation
 * @param {Object} object - DateTime object
 * @returns {string|boolean} DateTime string or false if invalid
 */
function objectToDateTimeString(object) {
	if (
		!hasOnlyProps(object, typeProperties.DateTime) &&
		!hasOnlyProps(object, typeProperties.DateTimeAlt)
	) {
		return false;
	}

	const total = object.timeZoneOffsetSeconds;
	const sign = total < 0 ? '-' : '+';
	const abs = Math.abs(total);

	const gmtHours = Math.floor(abs / 3600);
	const gmtMinutes = Math.floor((abs % 3600) / 60);

	return (
		`${object.year}-${_.padStart(object.month, 2, '0')}-${_.padStart(object.day, 2, '0')} ` +
		`${_.padStart(object.hour, 2, '0')}:${_.padStart(object.minute, 2, '0')}:${_.padStart(object.second, 2, '0')}.` +
		`${_.padEnd(object.nanosecond, 9, '0')} ` +
		`${sign}${_.padStart(gmtHours, 2, '0')}:${_.padStart(gmtMinutes, 2, '0')}`
	);
}

/**
 * Converts a date object to ISO date string
 * @param {Object} obj - Date object with year, month, day
 * @returns {string} ISO date string
 */
function objectToISODate(obj) {
	return `${obj.year}-${obj.month}-${obj.day}`;
}

/**
 * Converts a date-time object to JavaScript Date
 * @param {Object} obj - DateTime object
 * @returns {Date} JavaScript Date object
 */
function objectToLocalDateTime(obj) {
	return new Date(obj.year, obj.month-1, obj.day, obj.hour, obj.minute, obj.second);
}

/**
 * Converts a JavaScript Date to datetime object
 * @param {Date} date - JavaScript Date
 * @returns {Object} DateTime object
 */
function dateToDateTimeObject(date) {
	return {
		year: date.getFullYear(),
		month: date.getMonth() + 1,
		day: date.getDate(),
		hour: date.getHours(),
		minute: date.getMinutes(),
		second: date.getHours(),
		nanosecond: date.getMilliseconds() * 1000000,
	}
}

/**
 * Converts a JavaScript Date to date object
 * @param {Date} date - JavaScript Date
 * @returns {Object} Date object with year, month, day
 */
function dateToObject(date) {
	return {
		year: date.getFullYear(),
		month: date.getMonth() + 1,
		day: date.getDate(),
	}
}

/**
 * Parses an ISO time string to time object
 * @param {string} isoTime - ISO time string
 * @returns {Object} Time object
 */
function ISOTimeToObject(isoTime) {
	let timeParts = _.split(isoTime, ':');
	return {
		hour: _.toNumber(timeParts[0]),
		minute: _.toNumber(timeParts[1]) || 0,
		second: _.toNumber(timeParts[2]) || 0,
		nanosecond: 0
	}
}

/**
 * Validates a local time object
 * @param {Object} time - Time object to validate
 * @returns {boolean} True if valid
 */
function isValidLocalTimeObject(time) {
	if (   ! _.isPlainObject(time)
		|| ! _.isInteger(time.hour) || time.hour < 0 || time.hour > 24
		|| ! _.isInteger(time.minute) || time.minute < 0 || time.minute > 59
		|| ! _.isInteger(time.second) || time.second < 0 || time.second > 59
		|| ! _.isInteger(time.nanosecond) || time.nanosecond < 0 || time.nanosecond > 999999999
	) {
		return false;
	}

	return true;
}

/**
 * Validates a zoned time object
 * @param {Object} time - Time object to validate
 * @returns {boolean} True if valid
 */
function isValidTimeObject(time) {
	if (   ! _.isPlainObject(time)
		|| ! _.isInteger(time.hour) || time.hour < 0 || time.hour > 24
		|| ! _.isInteger(time.minute) || time.minute < 0 || time.minute > 59
		|| ! _.isInteger(time.second) || time.second < 0 || time.second > 59
		|| ! _.isInteger(time.nanosecond) || time.nanosecond < 0 || time.nanosecond > 999999999
		|| ! _.isInteger(time.timeZoneOffsetSeconds) || time.timeZoneOffsetSeconds < -43200 || time.timeZoneOffsetSeconds > 50400 // UTC-12 and UTC+14 are max deviations
	) {
		return false;
	}

	return true;
}

/**
 * Checks whether a value is a supported primitive type.
 *
 * property primitives:
 *  - String
 *  - Number (Integer / Float)
 *  - Boolean
 *
 * @param {*} value
 * @returns {boolean} True if the value can be stored as a property element
 */
function isPrimitive(value) {
	return (
		_.isString(value) ||
		_.isNumber(value) ||
		_.isBoolean(value)
	);
}

/**
 * Checks whether a JavaScript array can be stored as a array property.
 *
 * array rules:
 *  - All elements must be primitive (string, number, boolean)
 *  - All elements must be of the same type
 *
 * Valid:
 *   [1, 2, 3]
 *   ["a", "b"]
 *   [true, false]
 *
 * Invalid:
 *   [1, "a"]
 *   [{a:1}, {b:2}]
 *   [[1,2], [3,4]]
 *
 * @param {*} arr
 * @returns {boolean} True if array is  compatible
 */
function isPrimitiveArray(arr) {
	if (!Array.isArray(arr)) return false;

	//  allows empty arrays
	if (arr.length === 0) return true;

	const first = arr[0];
	const firstType = typeof first;

	if (!isPrimitive(first)) return false;

	return arr.every(v =>
		typeof v === firstType &&
		isPrimitive(v)
	);
}
export default {
	name: 'EntityFormView',
	mixins: [VueViewComponent],
	components: {
		"view-common": ViewCommon,
		codemirror,
		MaskedInput
	},
	data() {
		return {
			activeTab: 'edit',
			jsonProperties: '',
			editableEntity: {
				entityType: 'node', // 'node' or 'relation'
				labels: '',         // for nodes
				type: '',           // for relations
				source: null,       // for relations (from)
				target: null,       // for relations (to)
				properties: [],
				id: null,
				meta: {
					store: null
				}
			},
			messages: [], // Array of {name, type, text} for view-messages component
			messagesOverlay: true,
			messagesTimeout: 5000,
			initialSnapshot: null, // Stores initial state to detect changes
			initialEntitySnapshot: null, // Stores labels/type to track non-property edits in JSON tab
			initialJsonSnapshot: null, // Stores initial JSON text for JSON-tab change detection
			cmOptions: {
				tabSize: 4,
				mode: 'plain/text',
				lineNumbers: false,
				line: true,
				lineWrapping: true,
			},
			storeDriverMap: {},
			currentStoreDriver: null
		};
	},
	computed: {
		/**
		 * Returns true if editing a node entity
		 * @returns {boolean}
		 */
		isNode() {
			return this.editableEntity.entityType === 'node';
		},
		/**
		 * Returns true if editing a relation entity
		 * @returns {boolean}
		 */
		isRelation() {
			return this.editableEntity.entityType === 'relation';
		},
		/**
		 * Checks if editing a typed entity (IA_Function for nodes, TRIGGER for relations)
		 * @returns {boolean}
		 */
		isTypedEntity() {
			if (this.isNode) {
				return this.editableEntity.labels.includes('IA_Function');
			}
			return this.editableEntity.type === 'TRIGGER';
		},
		/**
		 * Converts current properties to JSON string
		 * @returns {string}
		 */
		propertiesAsJson() {
			const propsObject = _.reduce(
				this.editableEntity.properties,
				(res, prop) => {
					if (prop.key) {
						_.set(res, [prop.key], prop.lastValidValue);
					}
					return res;
				},
				{}
			);
			return JSON.stringify(propsObject, null, 2);
		},
		/**
		 * Checks if form has changes compared to initial state
		 * Empty properties (no key and no value) are excluded from comparison
		 * @returns {boolean}
		 */
		hasChanges() {
			if (! this.initialSnapshot) {
				return true;
			}

			if (this.activeTab === 'json') {
				if (this.getCurrentEntitySnapshot() !== this.initialEntitySnapshot) {
					return true;
				}

				return this.jsonProperties !== this.initialJsonSnapshot;
			}

			if (this.getCurrentSnapshot() !== this.initialSnapshot) {
				return true;
			}

			return false;
		},
		/**
		 * Returns the available data type options filtered by the selected store's driver
		 * @returns {Array<{value: string, label: string, title: string}>}
		 */
		availableDataTypeOptions() {
			const driver = this.currentStoreDriver;
			const unsupported = driver ? (driverUnsupportedTypes[driver] || []) : [];
			return allDataTypeOptions.filter(opt => {
				if (unsupported.includes(opt.value)) return false;
				if (driverOnlyTypes[opt.value] && !driverOnlyTypes[opt.value].includes(driver)) return false;
				return true;
			});
		}
	},
	watch: {
		'model.node': {
			handler(newNode) {
				if (newNode) {
					this.editableEntity.entityType = 'node';
					this.initializeData(newNode);
				}
			},
			deep: true,
			immediate: true
		},
		'model.relation': {
			handler(newRelation) {
				if (newRelation) {
					this.editableEntity.entityType = 'relation';
					this.initializeDataRelation(newRelation);
				}
			},
			deep: true,
			immediate: true
		},
		'model.id': {
			handler(id) {
				if (_.isNil(id)) {
					return;
				}
				this.editableEntity.entityType = this.model.entityType;
				this.loadEntity();
			},
		},
		'model.entityType': {
			handler(entityType) {
				if (_.isNil(entityType)) {
					return;
				}
				this.editableEntity.entityType = entityType;
			},
			deep: true,
			immediate: true
		},
		'model.store': {
			handler(store) {
				if (_.isNil(store)) {
					return;
				}
				// Set store if entity doesn't have one yet
				if (!this.editableEntity.meta.store) {
					this.editableEntity.meta.store = store;
					this.updateCurrentStoreDriver();
				}
			},
			immediate: true
		},
		'model.preset': {
			handler(preset) {
				if (!preset) {
					return;
				}
				// Skip preset initialization if node or relation is already provided
				if (this.model.node || this.model.relation) {
					return;
				}
				// If preset has from/to, it's a relation preset
				if (preset.from || preset.to) {
					this.initializeFromRelationPreset(preset);
				} else {
					// Otherwise treat as node preset
					this.initializeFromNodePreset(preset);
				}
			},
			deep: true,
			immediate: true
		},
		'editableEntity.type': {
			handler(newType, oldType) {
				if (!this.isRelation) {
					return;
				}
				// Only auto-add 'type' property when creating a new relation (no id)
				// and when the type is changed TO 'TRIGGER' (not when it stays TRIGGER)
				if (this.editableEntity.id === null && newType === 'TRIGGER' && oldType !== 'TRIGGER') {
					this.autoAddTypeProperty();
				}
			},
		},
		'editableEntity.labels': {
			handler(newLabels, oldLabels) {
				if (!this.isNode) {
					return;
				}
				// Only auto-add 'type' property when creating a new node (no id)
				// and when IA_Function is newly added to labels (not when it was already there)
				const newHasIA = newLabels && newLabels.includes('IA_Function');
				const oldHasIA = oldLabels && oldLabels.includes('IA_Function');
				if (this.editableEntity.id === null && newHasIA && !oldHasIA) {
					this.autoAddTypeProperty();
				}
			},
		},
	},

	onSetDependencies(dependencies) {
		this.hydrateStoreDriverMap(dependencies);
		this.updateCurrentStoreDriver();
	},

	mounted() {
		this.$api = this.$dependencies.get(IAPI);
		this.$language = this.$dependencies.get(Language);
		this.hydrateStoreDriverMap();
		this.updateCurrentStoreDriver();
	},

	methods: {

		onStoreSelect(storeName) {
			this.$set(this.editableEntity.meta, 'store', storeName);
			this.onStoreChange();
		},

		hydrateStoreDriverMap(dependencies = this.$dependencies) {
			if (!dependencies) {
				return this.storeDriverMap;
			}

			const appInfo = dependencies.get(ApplicationInfo);
			if (!appInfo || !appInfo.stores) {
				return this.storeDriverMap;
			}

			const map = {};
			_.forEach(appInfo.stores, (storeInfo, name) => {
				map[name] = storeInfo.driver || storeInfo.type;
			});
			this.storeDriverMap = map;
			return map;
		},

		getStoreDriver(storeName = _.get(this.editableEntity, 'meta.store')) {
			if (!storeName) {
				return null;
			}

			const knownDriver = this.storeDriverMap[storeName];
			if (knownDriver) {
				return knownDriver;
			}

			const hydratedMap = this.hydrateStoreDriverMap();
			return hydratedMap[storeName] || null;
		},

		updateCurrentStoreDriver() {
			this.currentStoreDriver = this.getStoreDriver();
			return this.currentStoreDriver;
		},

		onStoreChange() {
			this.updateCurrentStoreDriver();
			const availableValues = new Set(this.availableDataTypeOptions.map(opt => opt.value));
			_.forEach(this.editableEntity.properties, (prop) => {
				if (prop.type && !availableValues.has(prop.type)) {
					prop.type = 'auto';
				}
			});
		},

		/**
		 * Creates a snapshot of labels/type state
		 * @returns {string} JSON string representing current entity header state
		 */
		getCurrentEntitySnapshot() {
			return JSON.stringify({
				labels: this.editableEntity.labels,
				type: this.editableEntity.type
			});
		},

		/**
		 * Creates a snapshot of the current form state for change detection
		 * Empty properties (no key and no value) are excluded from the snapshot
		 * @returns {string} JSON string representing current state
		 */
		getCurrentSnapshot() {
			// Filter out empty properties (no key AND empty value)
			const nonEmptyProperties = _.filter(
				this.editableEntity.properties,
				prop => _.trim(prop.key) || !_.isEmpty(prop.inputValue)
			);
			
			const state = {
				labels: this.editableEntity.labels,
				type: this.editableEntity.type,
				properties: _.map(nonEmptyProperties, prop => ({
					key: prop.key,
					type: prop.type,
					value: prop.inputValue
				}))
			};
			return JSON.stringify(state);
		},

		/**
		 * Takes a snapshot of the current form state and stores it as initial state
		 */
		takeSnapshot() {
			this.$nextTick(() => {
				this.initialSnapshot = this.getCurrentSnapshot();
				this.initialEntitySnapshot = this.getCurrentEntitySnapshot();
				this.initialJsonSnapshot = this.propertiesAsJson;
			});
		},

		/**
		 * Checks if a property key is a special/reserved key
		 * @param {string} key - Property key to check
		 * @returns {boolean} True if key is special
		 */
		isSpecialKey(key) {
			return _.includes(['type', 'uuid'], key);
		},

		/**
		 * Gets CodeMirror options for a property
		 * @param {Object} prop - Property object
		 * @returns {Object} CodeMirror options
		 */
		getCmOptions(prop) {
			const lineCount = (_.isString(prop.inputValue) ? prop.inputValue : '').split('\n').length;
			let mode = this.cmOptions.mode;

			if (prop.type === 'json' || prop.type === 'object' || prop.type === 'array' || prop.type === 'point2d' || prop.type === 'point3d' || prop.type === 'duration' || prop.type === 'location') {
				mode = 'application/json';
			}

			if (_.startsWith(prop.key, '$') || _.startsWith(prop.key, '#')) {
				mode = 'text/javascript';
			}

			if (_.includes(['template', '$template', 'html', '$html'], prop.key)) {
				mode = 'text/html';
			}

			if (_.includes(['cypher', '$cypher', '#cypher'], prop.key)) {
				mode = 'application/x-cypher-query';
			}

			if (_.includes(['css', '$css'], prop.key)) {
				mode = 'text/css';
			}
			
			return {
				...this.cmOptions,
				lineNumbers: lineCount > 10,
				mode: mode
			};
		},

		/**
		 * Maps data type to input type
		 * @param {string} dataType - The data type
		 * @returns {string} The input type
		 */
		getInputType(dataType) {
			return _.get({
				auto: 'codeeditor',
				boolean: 'checkbox',
				number: 'number',
				string: 'codeeditor',
				date: 'date',
				datetime: 'datetime',
				localdatetime: 'localdatetime',
				localtime: 'localtime',
				time: 'time',
				array: 'codeeditor',
				object: 'codeeditor',
				fileupload: 'fileupload',
				json: 'codeeditor',
				point2d: 'codeeditor',
				point3d: 'codeeditor',
				location: 'codeeditor',
				duration: 'codeeditor',
			}, dataType, 'codeeditor')
		},

		/**
		 * Gets default parameters for the view
		 * @returns {Object} Default parameters
		 */
		getParameters() {
			return {
				node: null,
				relation: null,
				stores: [],
				preset: {},
				id: null,
				store: 'application'
			};
		},

		/**
		 * Initializes data for a node entity
		 * @param {Object} node - Node data
		 */
		initializeData(node = {labels: [], properties: {}, meta: {}}) {
			this.editableEntity.entityType = 'node';
			this.editableEntity.id = node.id;
			this.editableEntity.meta = node.meta || {};

			// Set default store if creating a new entity
			if (!this.editableEntity.id && !this.editableEntity.meta.store) {
				if (this.model && this.model.stores && this.model.stores.length > 0) {
					this.editableEntity.meta.store = this.model.stores[0];
				}
			}

			this.editableEntity.labels = _.isArray(node.labels) ? node.labels.join(',') : (node.labels || '');

			this.updateCurrentStoreDriver();
			this.initializeProperties(node.properties || {});
		},

		/**
		 * Initializes data for a relation entity
		 * @param {Object} relation - Relation data
		 */
		initializeDataRelation(relation = {type: '', properties: {}, meta: {}}) {
			this.editableEntity.entityType = 'relation';
			this.editableEntity.id = relation.id;
			this.editableEntity.meta = relation.meta || {};
			this.editableEntity.type = relation.type || '';
			this.editableEntity.source = _.isNil(relation.source) ? (_.isNil(relation.from) ? null : relation.from) : relation.source;
			this.editableEntity.target = _.isNil(relation.target) ? (_.isNil(relation.to) ? null : relation.to) : relation.target;

			// Set default store if creating a new entity
			if (!this.editableEntity.id && !this.editableEntity.meta.store) {
				if (this.model && this.model.stores && this.model.stores.length > 0) {
					this.editableEntity.meta.store = this.model.stores[0];
				}
			}
			
			this.updateCurrentStoreDriver();
			this.initializeProperties(relation.properties || {});
		},

		/**
		 * Initializes data for a relation from preset (handles from/to as node objects)
		 * @param {Object} preset - Preset data containing from/to nodes
		 */
		initializeFromRelationPreset(preset) {
			let language = this.$language ||  this.$dependencies.get(Language);
			
			let from = preset.from;
			let to = preset.to;
			
			// Store compatibility check - both nodes must be from the same store
			const fromStore = _.get(from, 'meta.store');
			const toStore = _.get(to, 'meta.store');
			
			if (fromStore && toStore && fromStore !== toStore) {
				throw new Error(language.translate('Both nodes must be from the same store.'));
			}
			
			this.editableEntity.entityType = 'relation';

			// Extract ID if from/to are node objects
			if (_.isObject(from)) {
				this.editableEntity.source = from.id;
			} else {
				this.editableEntity.source = from;
			}
			
			if (_.isObject(to)) {
				this.editableEntity.target = to.id;
			} else {
				this.editableEntity.target = to;
			}
			
			// Set store from nodes if not already set
			if (!this.editableEntity.meta.store) {
				this.editableEntity.meta.store = _.isNil(fromStore) ? (_.isNil(toStore) ? null : toStore) : fromStore;
			}

			this.editableEntity.id = _.isNil(preset.id) ? null : preset.id;
			this.editableEntity.type = preset.type || "";

			this.updateCurrentStoreDriver();
			this.initializeProperties(preset.properties || {});
		},

		/**
		 * Initializes data for a node from preset
		 * @param {Object} preset - Preset data containing labels, properties, etc.
		 */
		initializeFromNodePreset(preset) {
			this.editableEntity.entityType = 'node';
			this.editableEntity.id = _.isNil(preset.id) ? null : preset.id;

			// Handle labels - can be string or array
			let labels = preset.labels;
			if (_.isString(labels)) {
				// Parse comma-separated string to array, then join back
				this.editableEntity.labels = labels;
			} else if (_.isArray(labels)) {
				this.editableEntity.labels = labels.join(',');
			} else {
				this.editableEntity.labels = '';
			}
			
			// Set meta (excluding core)
			this.editableEntity.meta = _.omit(preset.meta || {}, ['core']);
			
			// Set store from model if not in preset
			if (!this.editableEntity.meta.store && this.model && this.model.store) {
				this.editableEntity.meta.store = this.model.store;
			}
			
			this.updateCurrentStoreDriver();
			this.initializeProperties(preset.properties || {});
		},

		/**
		 * Initializes properties from an object
		 * @param {Object} properties - Properties object
		 */
		initializeProperties(properties) {
			const driver = this.getStoreDriver() || this.currentStoreDriver;
			// Convert properties object to an array of {key, value, type} pairs
			this.editableEntity.properties = _.sortBy(
				_.map(properties, (value, key) => {
					const type = getValueType(value, driver);
					let displayValue = value;

					if (_.includes(['array', 'object', 'point2d', 'point3d', 'duration', 'location'], type)) {
						displayValue = toJSON(value);
					}

					else if (type === 'date') {
						displayValue = isoDate(new Date(objectToISODate(value)));
					}

					else if (type === 'localdatetime') {
						displayValue = isoDatetime(objectToLocalDateTime(value));
					}

					else if (type === 'localtime') {
						displayValue = objectToISOLocalTime(value);
					}

					else if (type === 'datetime') {
						displayValue = objectToDateTimeString(value);
					}

					else if (type === 'time') {
						displayValue = objectToTimeString(value);
					}

					return { key, type, value, inputValue: displayValue, lastValidValue: value };
				}), 
				(row) => {
					// Show 'type' property first for IA_Function nodes or TRIGGER relations
					if (this.isTypedEntity && row.key == 'type') {
						return '  type';
					};
					return row.key;
				}
			);

			this.jsonProperties = this.propertiesAsJson;

			// Add an empty property row for user convenience if no properties exist
			if (_.isEmpty(this.editableEntity.properties)) {
				this.editableEntity.properties.push({ key: '', value: '', type: 'auto', inputValue: '', lastValidValue: '' });
			}

			// Take snapshot of initial state for change detection
			this.takeSnapshot();
		},
		
		/**
		 * Adds a new empty property
		 */
		addProperty() {
			this.editableEntity.properties.push({ key: '', value: '', type: 'auto', inputValue: null, lastValidValue: null });
			
			// Focus on the new property's key input
			const newIndex = this.editableEntity.properties.length - 1;
			this.focusPropertyKey(newIndex);
		},

		/**
		 * Auto-adds a 'type' property for typed entities (IA_Function nodes or TRIGGER relations)
		 * Only adds if 'type' property doesn't already exist
		 */
		autoAddTypeProperty() {
			// Check if 'type' property already exists
			const hasTypeProperty = this.editableEntity.properties.some(p => p.key === 'type');
			
			if (!hasTypeProperty) {
				// Add 'type' property at the beginning of the array
				this.editableEntity.properties.unshift({ 
					key: 'type', 
					value: '', 
					type: 'string', 
					inputValue: '', 
					lastValidValue: '' 
				});
				
				// Focus on the new property's value field
				this.focusPropertyValue(0);
			}
		},

		/**
		 * Focuses on the value input of a property at the given index
		 * @param {number} index - Property index
		 */
		focusPropertyValue(index) {
			this.$nextTick(() => {
				const prop = this.editableEntity.properties[index];
				const inputType = this.getInputType(prop.type);
				
				// Handle CodeMirror (for codeeditor type)
				if (inputType === 'codeeditor') {
					const cmRef = this.$refs['cm-' + index];
					if (cmRef && cmRef[0] && cmRef[0].codemirror) {
						cmRef[0].codemirror.focus();
						return;
					}
				}
				
				// Handle other input types - find all property rows and focus the input in the value cell
				const propertyRows = this.$el.querySelectorAll('table tbody tr');
				if (propertyRows[index]) {
					const valueCell = propertyRows[index].querySelectorAll('td')[2]; // Third cell is the value
					if (valueCell) {
						const focusable = valueCell.querySelector('input, select, textarea, button');
						if (focusable) {
							focusable.focus();
						}
					}
				}
			});
		},

		/**
		 * Focuses on the key input of a property at the given index
		 * @param {number} index - Property index
		 */
		focusPropertyKey(index) {
			this.$nextTick(() => {
				const propertyRows = this.$el.querySelectorAll('table tbody tr');
				if (propertyRows[index]) {
					const keyCell = propertyRows[index].querySelectorAll('td')[0]; // First cell is the key
					if (keyCell) {
						const keyInput = keyCell.querySelector('input');
						if (keyInput) {
							keyInput.focus();
						}
					}
				}
			});
		},

		/**
		 * Removes a property at the given index
		 * @param {number} index - Index of property to remove
		 */
		removeProperty(index) {
			this.editableEntity.properties.splice(index, 1);
		},

		/**
		 * Handles blur/leave event on value input - auto-detects type if set to 'auto'
		 * Detects date/time formats, numbers, booleans, JSON objects/arrays
		 * @param {number} index - Property index
		 */
		onValueLeave(index) {
			const prop = this.editableEntity.properties[index];

			// Only auto-detect if type is 'auto'
			if (prop.type !== 'auto') {
				return;
			}

			// Skip if value is empty
			const trimmedValue = _.trim(_.toString(prop.inputValue));
			if (!trimmedValue) {
				return;
			}

			const currentStoreDriver = this.currentStoreDriver;
			
			const hasNums = (o, keys) => _.every(keys, k => _.has(o, k) && _.isNumber(o[k]));

			const inferSRID = (dim, crs) => {
				if (dim === 2 && crs === 'cartesian') return 7203;
				if (dim === 2 && crs === 'wgs84') return 4326;
				if (dim === 3 && crs === 'cartesian') return 9157;
				if (dim === 3 && crs === 'wgs84') return 4979;
				return undefined;
			};

			const pickGroup = (...vals) => _.find(vals, v => !_.isNil(v) && v !== '');

			function tryParseObjectString(str) {
				if (!_.isString(str)) return str;

				const cleaned = str
					.trim()
					.replace(/([{,]\s*)([a-zA-Z_]\w*)\s*:/g, '$1"$2":'); // key: → "key":

				try {
					return JSON.parse(cleaned);
				} catch {
					return str;
				}
			}


			function isDuration(value) {
				return isDurationValue(value);
			}

			function parseToDurationObject(value) {
				return parseDurationObject(value, currentStoreDriver);
			}

			function isPoint2D(value) {
				value = tryParseObjectString(value);

				// STRING FORMS
				if (_.isString(value)) {
					return (
						wkt2DReg.test(value) ||
						cypherPoint2DReg.test(value) ||
						driverPoint2DReg.test(value)
					);
				}

				// OBJECT FORMS
				if (_.isObject(value)) {
					const xy = hasNums(value, ['x', 'y']);
					const latlon = hasNums(value, ['latitude', 'longitude']);
					const sridOk = !_.has(value, 'srid') || _.isNumber(value.srid);

					return (xy || latlon) && sridOk;
				}

				return false;
			}

			function isPoint3D(value) {
				value = tryParseObjectString(value);
				// --- STRING FORMS ---

				if (!_.isPlainObject(value) && _.isString(value)) {
					return (
						wkt3DReg.test(value) ||
						cypherPoint3DReg.test(value) ||
						driverPoint3DReg.test(value)
					);
				}
				// --- OBJECT FORMS ---
				if (_.isPlainObject(value) || _.isObject(value)) {

					const hasXYZ =
					_.has(value, 'x') &&
					_.has(value, 'y') &&
					_.has(value, 'z') &&
					_.every(['x', 'y', 'z'], k => _.isFinite(Number(value[k])));

					const hasLLH =
					_.has(value, 'longitude') &&
					_.has(value, 'latitude') &&
					_.has(value, 'height') &&
					_.every(['longitude', 'latitude', 'height'], k => _.isFinite(Number(value[k])));

					const sridOk = !_.has(value, 'srid') || _.isFinite(Number(value.srid));

					return (hasXYZ || hasLLH) && sridOk;
				}

				return false;
			}

			function parseToPoint2DObject(value) {
				value = tryParseObjectString(value);

				// Object
				if (_.isObject(value)) {
					if ('longitude' in value && 'latitude' in value) {
						return {
							srid: value.srid !== undefined ? Number(value.srid) : inferSRID(2, 'wgs84'),
							x: Number(value.longitude),
							y: Number(value.latitude),
						};
					}
					if ('x' in value && 'y' in value) {
						return {
							srid: value.srid !== undefined ? Number(value.srid) : inferSRID(2, 'cartesian'),
							x: Number(value.x),
							y: Number(value.y),
						};
					}
					throw new Error('Invalid Point2D object');
				}

				if (!_.isString(value)) throw new Error('Invalid Point2D');

				let m;

				// WKT
				m = value.match(wkt2DReg);
				if (m) {
					return {
						srid: m.groups.srid ? Number(m.groups.srid) : inferSRID(2, 'cartesian'),
						x: Number(m.groups.x),
						y: Number(m.groups.y),
					};
				}

				// Cypher
				m = value.match(cypherPoint2DReg);
				if (m) {
				const isWgs = m.groups.lon || m.groups.lat;
					return {
						srid: m.groups.srid
						? Number(m.groups.srid)
						: inferSRID(2, isWgs ? 'wgs84' : 'cartesian'),
						x: Number(pickGroup(m.groups.x, m.groups.lon)),
						y: Number(pickGroup(m.groups.y, m.groups.lat)),
					};
				}

				// Driver
				m = value.match(driverPoint2DReg);
				if (m) {
					return {
						srid: Number(m.groups.srid),
						x: Number(m.groups.x),
						y: Number(m.groups.y),
					};
				}

				throw new Error('Invalid Point2D');
			}

			function parseToPoint3DObject(value) {
				value = tryParseObjectString(value);



				// Object
				if (_.isObject(value)) {
					if ('longitude' in value && 'latitude' in value && 'height' in value) {
						return {
							srid: value.srid !== undefined ? Number(value.srid) : inferSRID(3, 'wgs84'),
							x: Number(value.longitude),
							y: Number(value.latitude),
							z: Number(value.height),
						};
					}
					if ('x' in value && 'y' in value && 'z' in value) {
						return {
							srid: value.srid !== undefined ? Number(value.srid) : inferSRID(3, 'cartesian'),
							x: Number(value.x),
							y: Number(value.y),
							z: Number(value.z),
						};
					}
					throw new Error('Invalid Point3D object');
				}

				if (!_.isString(value)) throw new Error('Invalid Point3D');

				let m;

				// WKT
				m = value.match(wkt3DReg);
				if (m) {
					return {
						srid: m.groups.srid ? Number(m.groups.srid) : inferSRID(3, 'cartesian'),
						x: Number(m.groups.x),
						y: Number(m.groups.y),
						z: Number(m.groups.z),
					};
				}

				// Cypher
				m = value.match(cypherPoint3DReg);
				if (m) {
				const isWgs = m.groups.lon || m.groups.lat;
					return {
						srid: m.groups.srid
						? Number(m.groups.srid)
						: inferSRID(3, isWgs ? 'wgs84' : 'cartesian'),
						x: Number(pickGroup(m.groups.x, m.groups.lon)),
						y: Number(pickGroup(m.groups.y, m.groups.lat)),
						z: Number(pickGroup(m.groups.z, m.groups.h)),
					};
				}

				// Driver
				m = value.match(driverPoint3DReg);
				if (m) {
					return {
						srid: Number(m.groups.srid),
						x: Number(m.groups.x),
						y: Number(m.groups.y),
						z: Number(m.groups.z),
					};
				}

				throw new Error('Invalid Point3D');
			}

			function parseToLocationObject(value) {
				const point = parseToPoint2DObject(value);
				return { latitude: point.y, longitude: point.x };
			}

			let detectedType = 'string';
			
			// Date → 2026-02-04, 2026/02/04, 2026.02.04
			if (dateReg.test(trimmedValue)
			) {
				detectedType = 'date';
				prop.lastValidValue = trimmedValue;
			}
			// Zoned DateTime → 2026-02-04T12:30:15Z
			// 2026-02-04 12:30:15+03:00
			else if (zonedDateTimeReg.test(trimmedValue)) {
				detectedType = 'datetime';
				prop.lastValidValue = trimmedValue;
			}
			// Local DateTime → 2026-02-04T12:30
			// 2026.02.04 12:30:15
			else if (localDateTimeReg.test(trimmedValue)) {
				detectedType = 'localdatetime';
				prop.lastValidValue = trimmedValue;
			}
			// Zoned Time → 12:30Z , 12:30:15+03:00
			else if (zonedTimeReg.test(trimmedValue)) {
				detectedType = 'time';
				prop.lastValidValue = trimmedValue;
			}
			// Local Time → 12:30 , 12:30:15
			else if (localTimeReg.test(trimmedValue)) {
				detectedType = 'localtime';
				prop.lastValidValue = trimmedValue;
			}
			// Check for boolean strings
			else if (_.includes(['true', 'false'], _.toLower(trimmedValue))) {
				detectedType = 'boolean';
			}
			// Check for point3d strings (not for falkordb)
			else if (this.currentStoreDriver !== 'falkordb' && isPoint3D(trimmedValue)) {
				if (_.isString(trimmedValue)) {
					const parsed = parseToPoint3DObject(trimmedValue);
					prop.lastValidValue = parsed;
				}
				detectedType = 'point3d';
			}
			// Check for point2d / location strings (driver-aware)
			else if (isPoint2D(trimmedValue)) {
				if (this.currentStoreDriver === 'falkordb') {
					prop.lastValidValue = parseToLocationObject(trimmedValue);
					detectedType = 'location';
				} else {
					if (_.isString(trimmedValue)) {
						const parsed = parseToPoint2DObject(trimmedValue);
						prop.lastValidValue = parsed;
					}
					detectedType = 'point2d';
				}
			}
			// Check for number
			else if (!_.isNaN(parseFloat(trimmedValue)) && _.toString(parseFloat(trimmedValue)) === trimmedValue) {
				detectedType = 'number';
			}
			// Check for duration
			else if (isDuration(trimmedValue)) {
				detectedType = 'duration';
				if (_.isString(trimmedValue)) {
					const parsed = parseToDurationObject(trimmedValue);
					prop.lastValidValue = parsed;
				}
			}
			// Check for JSON object or array
			else if ((trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) || 
					 (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))) {
				try {
					const parsed = JSON.parse(trimmedValue);
					if (isPrimitiveArray(parsed)) {
						detectedType = 'array';
						prop.lastValidValue = parsed;
					} else {
						detectedType = 'json';
						prop.lastValidValue = toJSON(parsed);
					}
				} catch (e) {
					// Not valid JSON, keep as string
				}
			}
			
			prop.type = detectedType;
			
			// Only trigger type change for non-date/time types
			// Date/time types already have inputValue in correct format
			this.onTypeChange(detectedType, index);
			
		},

		/**
		 * Sets an error message for a field
		 * @param {string} field - Field name
		 * @param {string} error - Error message
		 */
		setFieldError(field = '', error) {
			this.messages.length = 0;
			const text = (!field ? '' : `${field}: `) + error;
			this.$nextTick(() => {
				this.messages.push({
					name: field ? field : 'error-' + Date.now(), 
					type: 'danger', 
					text,
					timeout: 5000
				});
		 	});
		},

		/**
		 * Clears all error messages
		 */
		clearError() {
			this.messages = _.filter(this.messages, m => m.type !== 'danger');
		},

		/**
		 * Sets a success message that auto-hides
		 * @param {string} message - Success message
		 */
		setSuccessMessage(message) {
			const name = 'success-' + Date.now();
			this.messages.push({ name, type: 'success', text: message, timeout: 2000 });
		},

		/**
		 * Loads entity data from the server
		 */
		loadEntity() {
			let api = this.$api ||  this.$dependencies.get(IAPI);
			
			if (this.isNode) {
				let request = api.loadNode(this.model.id, this.model.store);
				request.done((response) => {
					this.initializeData(response)
				})
			} else {
				let request = api.loadRelation(this.model.id, this.model.store);
				request.done((response) => {
					this.initializeDataRelation(response)
				})
			}
		},

		/**
		 * Saves the current entity
		 * @returns {Object} jQuery deferred request
		 */
		saveEntity() {
			let isValid = true;
			let checkedProp = null;
			if (this.activeTab == 'json') {
				if (! this.updatePropertiesFromJson(this.jsonProperties)) {
					return;
				}
			}

			// Filter out completely empty rows (both key and value are empty)
			this.editableEntity.properties = _.filter(
				this.editableEntity.properties,
				(prop) => (_.trim(prop.key) || !_.isEmpty(prop.inputValue))
			);

			// Check for properties with value but no key
			const hasEmptyKey = this.editableEntity.properties.some((p) => !_.trim(p.key) && !_.isEmpty(p.inputValue));
			if (hasEmptyKey) {
				this.setFieldError('', 'Not all properties have a name');
				return;
			}

			const keys = this.editableEntity.properties.map(p => p.key);
			const distinctKeys = new Set(keys);

			if (keys.length !== distinctKeys.size) {
				this.setFieldError('', 'Property names must be distinct.');
				return;
			}

			_.forEach(
				this.editableEntity.properties,
				(prop, index) => {
					if (prop.type === 'auto') {
						this.onValueLeave(index);
					}

					const currentProp = this.editableEntity.properties[index];
					this.onValueChange(currentProp.inputValue, index);
					if (_.isNil(currentProp.value)) {
						checkedProp = currentProp;
						isValid = false;
						return;
					}
				}
			)
			if (!isValid) {
				this.setFieldError(checkedProp.key, 'Invalid value on field');
				return;
			}

			// Convert properties array back to an object
			const properties = _.reduce(
				this.editableEntity.properties,
				(res, prop) => _.set(res, [prop.key], prop.value),
				{}
			)

			let finalEntity;

			if (this.isNode) {
				finalEntity = {
					id: this.editableEntity.id,
					labels: this.editableEntity.labels.split(',').map(l => l.trim()).filter(Boolean),
					properties: properties,
					meta: _.get(this.editableEntity, 'meta', {})
				};
			} else {
				finalEntity = {
					id: this.editableEntity.id,
					type: this.editableEntity.type,
					source: this.editableEntity.source,
					target: this.editableEntity.target,
					from: this.editableEntity.source,
					to: this.editableEntity.target,
					properties: properties,
					meta: _.get(this.editableEntity, 'meta', {})
				};
			}

			let request = this.$api.save(finalEntity);

			request.fail((response) => {
				this.setFieldError('', `${response.message}<br/>${response.cause}`);
			});

			request.done((response) => {
				const eventType = _.isNil(this.editableEntity.id) ? 'created' : 'updated';
				const eventData = { entityType: this.editableEntity.entityType };
				
				if (this.isNode) {
					eventData.node = response;
					this.model.node = response;
				} else {
					eventData.relation = response;
					this.model.relation = response;
				}
				eventData.type= 'submitted';
				this.trigger(eventData);
				eventData.type= eventType;
				this.trigger(eventData);
			});

			return request;
		},

		/**
		 * Handles type change for a property
		 * @param {string} newType - New type value
		 * @param {number} index - Property index
		 */
		onTypeChange(newType, index) {
			let prop = this.editableEntity.properties[index];

			if (newType == 'string') {
				if (_.isFinite(prop.lastValidValue) || _.isBoolean(prop.lastValidValue)) {
					prop.inputValue = _.toString(prop.lastValidValue);
				}

				if (_.isString(prop.lastValidValue)) {
					prop.inputValue = prop.lastValidValue;
				}

				return;
			}

			if (newType == 'number') {
				return;
			}

			if (newType == 'boolean') {
				prop.inputValue = _.isBoolean(prop.lastValidValue) 
					? prop.lastValidValue
					: _.includes(['1', 'true'], _.toLower(_.trim(_.toString(prop.lastValidValue))));
				return;
			}

			if (newType == 'array') {
				prop.inputValue = (_.isArray(prop.lastValidValue) || _.isString(prop.lastValidValue) || _.isFinite(prop.lastValidValue))
					? toJSON(_.isArray(prop.lastValidValue) ? prop.lastValidValue : [prop.lastValidValue])
					: '';
				return;
			}

			if (newType == 'object') {
				prop.inputValue = _.isPlainObject(prop.lastValidValue)
					? toJSON(prop.lastValidValue)
					: prop.lastValidValue
				return;
			}

			if (newType == 'date') {
				if (_.isPlainObject(prop.lastValidValue)) {
					prop.inputValue = isoDate(new Date(objectToISODate(prop.lastValidValue)));
				}

				if (_.isString(prop.lastValidValue)) {
					prop.inputValue = isoDate(new Date(prop.lastValidValue));
				}
				return;
			}

			if (newType == 'datetime') {
				if (_.isPlainObject(prop.lastValidValue)) {
					prop.inputValue = objectToDateTimeString(prop.lastValidValue);
				}

				if (_.isString(prop.lastValidValue)) {

					let dateTimeObject = dateTimeStringToObject(prop.lastValidValue);

					if (dateTimeObject) {
						prop.inputValue = objectToDateTimeString(dateTimeObject);
						return;
					}

					let asDate = new Date(prop.lastValidValue);

					if (! _.isNaN(asDate)) { 
						prop.inputValue = objectToDateTimeString(_.extend(dateToDateTimeObject(asDate), {timeZoneOffsetSeconds: 0}));
					}
				}

				return;
			}

			if (newType == 'time') {
				if (_.isPlainObject(prop.lastValidValue)) {
					prop.inputValue = objectToTimeString(prop.lastValidValue);
				}

				if (_.isString(prop.lastValidValue)) {

					let timeObject = timeStringToObject(prop.lastValidValue);
					if (timeObject) {
						prop.inputValue = objectToTimeString(timeObject);
						return;
					}

					let asLocalTime = ISOTimeToObject(prop.lastValidValue);
					if (isValidLocalTimeObject(asLocalTime)) {
						prop.inputValue = objectToTimeString(_.extend(asLocalTime, {timeZoneOffsetSeconds: 0}));
					}
				}

				return;
			}


			if (newType == 'localdatetime') {
				if (_.isPlainObject(prop.lastValidValue)) {
					prop.inputValue = isoDatetime(objectToLocalDateTime(prop.lastValidValue));
				}

				if (_.isString(prop.lastValidValue)) {
					prop.inputValue = isoDatetime(new Date(prop.lastValidValue));
				}

				return;
			}

			if (newType == 'localtime') {
				if (_.isPlainObject(prop.lastValidValue)) {
					prop.inputValue = objectToISOLocalTime(prop.lastValidValue);
				}

				if (_.isString(prop.lastValidValue) && isValidLocalTimeObject(ISOTimeToObject(stringToISOLocalTime(prop.lastValidValue)))) {
					prop.inputValue = stringToISOLocalTime(prop.lastValidValue);
				}

				return;
			}

			if (newType == 'fileupload') {
				return;
			}

			if (newType == 'json') {
				if (_.isPlainObject(prop.lastValidValue) || _.isArray(prop.lastValidValue)) {
					prop.inputValue = toJSON(prop.lastValidValue);
				}

				return;
			}

			if (newType == 'point2d') {
				if (_.isPlainObject(prop.lastValidValue) && hasOnlyPropsSubset(prop.lastValidValue, typeProperties.Point2D)) {
					prop.inputValue = toJSON(prop.lastValidValue);
				} else {
					prop.inputValue = toJSON(_.zipObject(typeProperties.ValidPoint2D, _.fill(Array(typeProperties.ValidPoint2D.length), 0)));
				}
				return;
			}

			if (newType == 'point3d') {
				if (_.isPlainObject(prop.lastValidValue) && hasOnlyPropsSubset(prop.lastValidValue, typeProperties.Point3D)) {
					prop.inputValue = toJSON(prop.lastValidValue);
				} else {
					prop.inputValue = toJSON(_.zipObject(typeProperties.ValidPoint3D, _.fill(Array(typeProperties.ValidPoint3D.length), 0)));
				}

				return;
			}

			if (newType == 'location') {
				if (_.isPlainObject(prop.lastValidValue) && hasOnlyPropsSubset(prop.lastValidValue, typeProperties.Location)) {
					prop.inputValue = toJSON(prop.lastValidValue);
				} else {
					prop.inputValue = toJSON({ latitude: 0, longitude: 0 });
				}
				return;
			}

			if (newType == 'duration') {
				if (_.isPlainObject(prop.lastValidValue) && hasOnlyPropsSubset(prop.lastValidValue, typeProperties.Duration)) {
					prop.inputValue = toJSON(prop.lastValidValue);
				} else {
					let durationKeys = this.currentStoreDriver === 'falkordb'
						? typeProperties.ValidDurationFalkorDB
						: typeProperties.ValidDuration;
					prop.inputValue = toJSON(_.zipObject(durationKeys, _.fill(Array(durationKeys.length), 0)));
				}

				return;
			}

		},

		/**
		 * Handles value change for a property
		 * @param {*} value - New value
		 * @param {number} index - Property index
		 */
		onValueChange(value, index) {
			this.clearError();
			let prop = this.editableEntity.properties[index];

			if(prop.type === 'string' || prop.type === 'fileupload' || prop.type === 'auto') {
				prop.value = _.toString(value);
				prop.lastValidValue = prop.value;
			}

			if(prop.type === 'boolean') {
				prop.value = _.isBoolean(value) ? value : _.includes(['1', 'true'], _.toLower(_.trim(_.toString(value))));
				prop.lastValidValue = prop.value;
				return;
			}

			if(prop.type === 'number') {
				let valueAsNumber = parseFloat(value);

				if (! _.isNaN(valueAsNumber) && _.toString(valueAsNumber) === _.toString(value)) {
					prop.value = valueAsNumber;
					prop.lastValidValue = valueAsNumber;
				}
				else {
					this.setFieldError(prop.key, 'invalid number');
					prop.value = null;
					return;
				}
			}

			if (prop.type == 'array') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				if (_.isArray(parsedValue)) {
					prop.value = parsedValue;
					prop.lastValidValue = parsedValue;
				}
				else {
					this.setFieldError(prop.key, 'value must be an array');
					prop.value = null;
					return;
				}
			}

			if (prop.type == 'object') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				if (_.isPlainObject(parsedValue)) {
					prop.value = parsedValue;
					prop.lastValidValue = parsedValue;
				}
				else {
					this.setFieldError(prop.key, 'value must be an object');
					prop.value = null;
					return;
				}
			}
			
			if (prop.type == 'json') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				prop.value = value;
				prop.lastValidValue = value;
			}

			if(prop.type === 'date') {
				let valueAsDate = new Date(value);

				if (! isNaN(valueAsDate)) {
					prop.value = dateToObject(valueAsDate);
					prop.lastValidValue = prop.value;
				}
				else {
					this.setFieldError(prop.key, 'invalid date');
					prop.value = null;
					return;
				}
			}

			if(prop.type === 'time') {
				let timeObject = timeStringToObject(value)
				if (isValidTimeObject(timeObject)) {
					prop.value = timeObject;
					prop.lastValidValue = prop.value;
				}
				else {
					this.setFieldError(prop.key, 'invalid zoned time');
					prop.value = null;
					return;
				}
			}

			if(prop.type === 'localtime') {
				if (isValidLocalTimeObject(ISOTimeToObject(value))) {
					prop.value = ISOTimeToObject(value);
					prop.lastValidValue = prop.value;
				}
				else {
					this.setFieldError(prop.key, 'invalid local time');
					prop.value = null;
					return;
				}
			}

			if(prop.type === 'localdatetime') {
				let valueAsDate = new Date(value);

				if (! isNaN(valueAsDate)) {
					prop.value = dateToDateTimeObject(valueAsDate);
					prop.lastValidValue = prop.value;
				} 
				else {
					this.setFieldError(prop.key, 'invalid local date-time');
					prop.value = null;
					return;
				}
			}

			if(prop.type === 'datetime') {
				let valueAsObject = dateTimeStringToObject(value);

				if (valueAsObject) {
					prop.value = valueAsObject;
					prop.lastValidValue = prop.value;
				}
				else {
					this.setFieldError(prop.key, 'invalid zoned date-time');
					prop.value = null;
					return;
				}
			}

			if(prop.type === 'point2d') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				if (_.isPlainObject(parsedValue) && !hasOnlyPropsSubset(parsedValue, typeProperties.Point2D)) {
					this.setFieldError(prop.key, `value must be a Point 2D object allowed properties: ${_.join(typeProperties.Point2D, ', ')}`);
					prop.value = null;
					return;
				}
				if (!hasOnlyProps(parsedValue, typeProperties.ValidPoint2D)) {
					this.setFieldError(prop.key, `value must be a Point 2D object with properties: ${_.join(typeProperties.ValidPoint2D, ', ')}`);
					prop.value = null;
					return;	
					
				}
				if (!_.includes([7203,4326], parsedValue.srid)) {
					this.setFieldError(prop.key, `srid value can be one of 7203, 4326`);
					prop.value = null;
					return;	
				}
				prop.value = parsedValue;
				prop.lastValidValue = parsedValue;
			}

			if(prop.type === 'point3d') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				if (_.isPlainObject(parsedValue) && !hasOnlyPropsSubset(parsedValue, typeProperties.Point3D)) {
					this.setFieldError(prop.key, `value must be a Point 3D object allowed properties: ${_.join(typeProperties.Point3D, ', ')}`);
					prop.value = null;
					return;
				}
				if (!hasOnlyProps(parsedValue, typeProperties.ValidPoint3D)) {
					this.setFieldError(prop.key, `value must be a Point 3D object with properties: ${_.join(typeProperties.ValidPoint3D, ', ')}`);
					prop.value = null;
					return;
				}
				if (!_.includes([9157,4979], parsedValue.srid)) {
					this.setFieldError(prop.key, `srid value can be one of 9157, 4979`);
					prop.value = null;
					return;	
				}
				prop.value = parsedValue;
				prop.lastValidValue = parsedValue;
			}

			if(prop.type === 'location') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				if (!_.isPlainObject(parsedValue) || !hasOnlyProps(parsedValue, typeProperties.Location)) {
					this.setFieldError(prop.key, 'value must be a Location object with properties: latitude, longitude');
					prop.value = null;
					return;
				}
				if (!_.isFinite(parsedValue.latitude) || !_.isFinite(parsedValue.longitude)) {
					this.setFieldError(prop.key, 'latitude and longitude must be numbers');
					prop.value = null;
					return;
				}
				prop.value = parsedValue;
				prop.lastValidValue = parsedValue;
			}

			if(prop.type === 'duration') {
				let parsedValue;
				try {
					parsedValue = JSON.parse(value);
				}
				catch(ex) {
					this.setFieldError(prop.key, ex.message);
					prop.value = null;
					return;
				}

				if (_.isPlainObject(parsedValue) && hasOnlyPropsSubset(parsedValue, typeProperties.Duration)) {
					if (	hasOnlyProps(parsedValue, typeProperties.ValidDuration)
						||  hasOnlyProps(parsedValue, typeProperties.ValidDurationMemgraph)
						||  hasOnlyProps(parsedValue, typeProperties.ValidDurationFalkorDB)) {
						prop.value = parsedValue;
						prop.lastValidValue = parsedValue;
					} else {
						let expectedKeys = this.currentStoreDriver === 'falkordb'
							? typeProperties.ValidDurationFalkorDB
							: typeProperties.ValidDuration;
						this.setFieldError(prop.key, `value must be a Duration object with properties: ${_.join(expectedKeys, ', ')}`);
						prop.value = null;
						return;
					}
				}
				else {
					this.setFieldError(prop.key, `value must be a valid Duration object allowed properties: ${_.join(typeProperties.Duration, ', ')}`);
					prop.value = null;
					return;
				}
			}
		},

		/**
		 * Triggers file upload input click
		 * @param {number} index - Property index
		 */
		triggerFileUpload(index) {
			document.getElementById('file-upload-' + index).click();
		},

		/**
		 * Handles file upload change event
		 * @param {Event} event - Change event
		 * @param {number} index - Property index
		 */
		onFileChange(event, index) {
			const file = event.target.files[0];
			const prop = this.editableEntity.properties[index];

			if (!file) {
				return;
			}

			this.$api.upload(file)
			.done((data) => {
				const fileUrl = data.download_link;
				if (fileUrl) {
					prop.inputValue = fileUrl;
					this.onValueChange(fileUrl, index);
					this.setSuccessMessage('File uploaded successfully');
				} else {
					this.setFieldError(prop.key, 'URL not found in response');
				}
			})
			.fail((error) => {
				this.setFieldError(prop.key, error);
			});
		},

		/**
		 * Handles tab switch between Edit and JSON
		 * @param {string} newTab - New tab name
		 */
		onTabSwitch(newTab) {
			if (this.activeTab === newTab) return;

			if (this.activeTab === 'edit' && newTab === 'json') {
				// Sync from Edit to JSON
				// Filter out completely empty rows (both key and value are empty)
				const nonEmptyProperties = _.filter(
					this.editableEntity.properties,
					(p) => (_.trim(p.key) || !_.isEmpty(p.inputValue))
				);
				// Check for properties with value but no key
				if (_.some(nonEmptyProperties, (p) => !_.trim(p.key) && !_.isEmpty(p.inputValue))) {
					this.setFieldError(false, 'Not all properties have a name');
					return;
				}
				this.jsonProperties = this.propertiesAsJson;
				this.activeTab = newTab;
			} 
			else if (this.activeTab === 'json' && newTab === 'edit') {
				// Sync from JSON to Edit
				if (!this.updatePropertiesFromJson(this.jsonProperties)) {
					return;
				}
				this.activeTab = newTab;
			}

			this.clearError();
		},

		/**
		 * Updates properties from JSON string
		 * @param {string} newJson - JSON string
		 */
		updatePropertiesFromJson(newJson) {
			try {
				const newProps = JSON.parse(newJson);
				if (!_.isPlainObject(newProps)) {
					this.setFieldError('JSON', 'must be an object.');
					return false;
				}

				const driver = this.getStoreDriver() || this.currentStoreDriver;
				this.editableEntity.properties = _.map(newProps, (value, key) => {
					const type = getValueType(value, driver);
					let displayValue = value;

					if (_.includes(['array', 'object', 'point2d', 'point3d', 'duration', 'location'], type)) {
						displayValue = toJSON(value);
					}

					else if (type === 'date') {
						displayValue = isoDate(new Date(objectToISODate(value)));
					}

					else if (type === 'datetime') {
						displayValue = objectToDateTimeString(value);
					}

					else if (type === 'localdatetime') {
						displayValue = isoDatetime(objectToLocalDateTime(value));
					}

					else if (type === 'localtime') {
						displayValue = objectToISOLocalTime(value);
					}

					else if (type === 'time') {
						displayValue = objectToTimeString(value);
					}

					return { key, type, value, inputValue: displayValue, lastValidValue: value };
				});

				// Add an empty property row for user convenience if no properties exist
				if (_.isEmpty(this.editableEntity.properties)) {
					this.editableEntity.properties.push({ key: '', value: '', type: 'string', inputValue: '', lastValidValue: '' });
				}

				this.clearError();
				return true;
			} catch (e) {
				this.setFieldError('JSON', e.message);
				return false;
			}
		},

		/**
		 * Handles save button click
		 */
		onSave() {
			let request = this.saveEntity();

			if (request) {
				request.done((response) => {
					this.close();
				});
			}
		},

		/**
		 * Handles apply button click
		 */
		onApply() {
			let request = this.saveEntity();

			if (request) {
				request.done((response) => {
					this.setSuccessMessage('Changes applied successfully!');
				});
			}
		},

		/**
		 * Handles cancel button click
		 */
		cancel() {
			this.close();
		},


		/**
		 * Handles close message
		 */
		onCloseMessage(name) {
			setTimeout(() => {
				this.messages = _.filter(this.messages, m => m.name !== name);
			}, 0);
		}
	}
};
